// =============================================================================
// DAMS — Hybrid Storage Repository
// Dev: IndexedDB · Prod: Cloudflare D1 via Pages Functions
// =============================================================================

import type {
  Computer, ComputerSoftware, Printer, MobileDevice, OtherEquipment,
  NetworkDevice, CitySystem, NetworkSurveyItem, SecuritySurveyItem,
  StorageRepository, SyncLog, ApiResponse, BulkUpsertPayload,
} from './types';

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------
const DB_NAME    = 'dams_iloilo';
const DB_VERSION = 2;

const STORES = {
  computers:        'computers',
  computerSoftware: 'computer_software',
  printers:         'printers',
  mobileDevices:    'mobile_devices',
  otherEquipment:   'other_equipment',
  networkDevices:   'network_devices',
  systems:          'systems',
  networkSurvey:    'network_survey',
  securitySurvey:   'security_survey',
  syncLogs:         'sync_log',
} as const;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = (e.target as IDBOpenDBRequest).result;
      const create = (name: string, key: string) => {
        if (!db.objectStoreNames.contains(name))
          db.createObjectStore(name, { keyPath: key });
      };
      const createAI = (name: string) => {
        if (!db.objectStoreNames.contains(name))
          db.createObjectStore(name, { keyPath: 'id', autoIncrement: true });
      };
      create(STORES.computers,        'computer_id');
      create(STORES.computerSoftware, 'id');
      create(STORES.printers,         'printer_id');
      create(STORES.mobileDevices,    'mobile_id');
      create(STORES.otherEquipment,   'equipment_id');
      create(STORES.networkDevices,   'device_id');
      create(STORES.systems,          'system_id');
      createAI(STORES.networkSurvey);
      createAI(STORES.securitySurvey);
      create(STORES.syncLogs, 'sync_id');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

function getAll<T>(db: IDBDatabase, store: string): Promise<T[]> {
  return new Promise((res, rej) => {
    const r = db.transaction(store, 'readonly').objectStore(store).getAll();
    r.onsuccess = () => res(r.result as T[]);
    r.onerror   = () => rej(r.error);
  });
}

function putAll<T>(db: IDBDatabase, store: string, records: T[]): Promise<void> {
  return new Promise((res, rej) => {
    const tx = db.transaction(store, 'readwrite');
    const st = tx.objectStore(store);
    records.forEach(r => st.put(r));
    tx.oncomplete = () => res();
    tx.onerror    = () => rej(tx.error);
  });
}

function clearStore(db: IDBDatabase, store: string): Promise<void> {
  return new Promise((res, rej) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).clear();
    tx.oncomplete = () => res();
    tx.onerror    = () => rej(tx.error);
  });
}

function deleteKey(db: IDBDatabase, store: string, key: IDBValidKey): Promise<void> {
  return new Promise((res, rej) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(key);
    tx.oncomplete = () => res();
    tx.onerror    = () => rej(tx.error);
  });
}

// ---------------------------------------------------------------------------
// IndexedDB Repository (Dev)
// ---------------------------------------------------------------------------
class IndexedDbRepository implements StorageRepository {
  private _db = openDb();
  private async db() { return this._db; }

  async getComputers()         { return getAll<Computer>(await this.db(), STORES.computers); }
  async saveComputers(r: Computer[]) {
    const db = await this.db();
    await clearStore(db, STORES.computers);
    await putAll(db, STORES.computers, r);
  }
  async deleteComputer(id: string) { await deleteKey(await this.db(), STORES.computers, id); }

  async getComputerSoftware()  { return getAll<ComputerSoftware>(await this.db(), STORES.computerSoftware); }
  async saveComputerSoftware(r: ComputerSoftware[]) {
    const db = await this.db();
    await clearStore(db, STORES.computerSoftware);
    await putAll(db, STORES.computerSoftware, r);
  }

  async getPrinters()          { return getAll<Printer>(await this.db(), STORES.printers); }
  async savePrinters(r: Printer[]) {
    const db = await this.db();
    await clearStore(db, STORES.printers);
    await putAll(db, STORES.printers, r);
  }

  async getMobileDevices()     { return getAll<MobileDevice>(await this.db(), STORES.mobileDevices); }
  async saveMobileDevices(r: MobileDevice[]) {
    const db = await this.db();
    await clearStore(db, STORES.mobileDevices);
    await putAll(db, STORES.mobileDevices, r);
  }

  async getOtherEquipment()    { return getAll<OtherEquipment>(await this.db(), STORES.otherEquipment); }
  async saveOtherEquipment(r: OtherEquipment[]) {
    const db = await this.db();
    await clearStore(db, STORES.otherEquipment);
    await putAll(db, STORES.otherEquipment, r);
  }

  async getNetworkDevices()    { return getAll<NetworkDevice>(await this.db(), STORES.networkDevices); }
  async saveNetworkDevices(r: NetworkDevice[]) {
    const db = await this.db();
    await clearStore(db, STORES.networkDevices);
    await putAll(db, STORES.networkDevices, r);
  }

  async getSystems()           { return getAll<CitySystem>(await this.db(), STORES.systems); }
  async saveSystems(r: CitySystem[]) {
    const db = await this.db();
    await clearStore(db, STORES.systems);
    await putAll(db, STORES.systems, r);
  }

  async getNetworkSurvey()     { return getAll<NetworkSurveyItem>(await this.db(), STORES.networkSurvey); }
  async saveNetworkSurvey(r: NetworkSurveyItem[]) {
    const db = await this.db();
    await clearStore(db, STORES.networkSurvey);
    await putAll(db, STORES.networkSurvey, r);
  }

  async getSecuritySurvey()    { return getAll<SecuritySurveyItem>(await this.db(), STORES.securitySurvey); }
  async saveSecuritySurvey(r: SecuritySurveyItem[]) {
    const db = await this.db();
    await clearStore(db, STORES.securitySurvey);
    await putAll(db, STORES.securitySurvey, r);
  }

  async getSyncLogs()          { return getAll<SyncLog>(await this.db(), STORES.syncLogs); }
  async syncReport(log: SyncLog) { await putAll(await this.db(), STORES.syncLogs, [log]); }

  async clearAll() {
    const db = await this.db();
    await Promise.all(Object.values(STORES).map(s => clearStore(db, s)));
  }
}

// ---------------------------------------------------------------------------
// D1 Repository (Prod) — routes through Cloudflare Pages Functions
// ---------------------------------------------------------------------------
async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) throw new Error(`API ${path} → ${res.status}: ${await res.text()}`);
  const body: ApiResponse<T> = await res.json();
  if (!body.success) throw new Error(body.error ?? 'API error');
  return body.data as T;
}

class D1Repository implements StorageRepository {
  private async get<T>(table: string): Promise<T[]> {
    return api<T[]>(`/api/assets?table=${table}`);
  }
  private async post(table: string, records: unknown[]): Promise<void> {
    await api('/api/assets', { method: 'POST', body: JSON.stringify({ table, records }) });
  }

  getComputers(): Promise<Computer[]>              { return this.get<Computer>('computers'); }
  async saveComputers(r: Computer[]): Promise<void>{ await this.post('computers', r); }
  async deleteComputer(id: string): Promise<void> {
    await api(`/api/assets?table=computers&id=${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  getComputerSoftware(): Promise<ComputerSoftware[]>               { return this.get<ComputerSoftware>('computer_software'); }
  async saveComputerSoftware(r: ComputerSoftware[]): Promise<void> { await this.post('computer_software', r); }

  getPrinters(): Promise<Printer[]>              { return this.get<Printer>('printers'); }
  async savePrinters(r: Printer[]): Promise<void>{ await this.post('printers', r); }

  getMobileDevices(): Promise<MobileDevice[]>                { return this.get<MobileDevice>('mobile_devices'); }
  async saveMobileDevices(r: MobileDevice[]): Promise<void>  { await this.post('mobile_devices', r); }

  getOtherEquipment(): Promise<OtherEquipment[]>               { return this.get<OtherEquipment>('other_equipment'); }
  async saveOtherEquipment(r: OtherEquipment[]): Promise<void> { await this.post('other_equipment', r); }

  getNetworkDevices(): Promise<NetworkDevice[]>               { return this.get<NetworkDevice>('network_devices'); }
  async saveNetworkDevices(r: NetworkDevice[]): Promise<void> { await this.post('network_devices', r); }

  getSystems(): Promise<CitySystem[]>              { return this.get<CitySystem>('systems'); }
  async saveSystems(r: CitySystem[]): Promise<void>{ await this.post('systems', r); }

  getNetworkSurvey(): Promise<NetworkSurveyItem[]>                { return this.get<NetworkSurveyItem>('network_survey'); }
  async saveNetworkSurvey(r: NetworkSurveyItem[]): Promise<void>  { await this.post('network_survey', r); }

  getSecuritySurvey(): Promise<SecuritySurveyItem[]>               { return this.get<SecuritySurveyItem>('security_survey'); }
  async saveSecuritySurvey(r: SecuritySurveyItem[]): Promise<void> { await this.post('security_survey', r); }

  getSyncLogs(): Promise<SyncLog[]>              { return this.get<SyncLog>('sync_log'); }
  async syncReport(log: SyncLog): Promise<void> {
    await api('/api/assets/sync', { method: 'POST', body: JSON.stringify(log) });
  }
  async clearAll(): Promise<void> {
    await api('/api/assets/clear', { method: 'DELETE' });
  }
}

// ---------------------------------------------------------------------------
// Bulk D1 sync helper
// ---------------------------------------------------------------------------
export async function bulkSyncToD1(payload: BulkUpsertPayload): Promise<void> {
  await api('/api/assets/bulk', { method: 'POST', body: JSON.stringify(payload) });
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------
let _repo: StorageRepository | undefined;

export function getRepository(): StorageRepository {
  if (_repo) return _repo;
  const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
  _repo = isDev ? new IndexedDbRepository() : new D1Repository();
  return _repo;
}

export function getStorageMode(): { mode: 'indexeddb' | 'd1'; label: string } {
  const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
  return isDev
    ? { mode: 'indexeddb', label: 'Local IndexedDB (Dev Mode)' }
    : { mode: 'd1',        label: 'Cloudflare D1 (Production)' };
}
